import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { SwipeCombination } from "@/lib/swipeSession";

// These match the local string-literal types in Wardrobe.tsx / ContextSheet.tsx.
// Kept as plain `string` here to avoid a cross-file type-export refactor.
type OccasionKey = string;
type WeatherKey = string;
type MoodKey = string;

type Args = {
  userId: string;
  occasion: OccasionKey;
  weather: WeatherKey;
  mood: MoodKey;
  enabled: boolean;
  excludeItemIds?: string[];
};

export const wardrobeCombinationsKey = (args: {
  userId: string;
  occasion: OccasionKey;
  weather: WeatherKey;
  mood: MoodKey;
}) =>
  [
    "wardrobe-combinations",
    args.userId,
    args.occasion,
    args.weather,
    args.mood,
  ] as const;

export function useWardrobeCombinations(args: Args) {
  return useQuery({
    queryKey: wardrobeCombinationsKey(args),
    queryFn: async (): Promise<SwipeCombination[]> => {
      const { data, error } = await supabase.functions.invoke(
        "style-swipe-batch",
        {
          body: {
            occasion: args.occasion,
            weather: args.weather,
            mood: args.mood,
            count: 10,
            exclude_item_ids: args.excludeItemIds ?? [],
          },
        },
      );
      if (error) throw error;
      return ((data as { combinations?: SwipeCombination[] })?.combinations ??
        []) as SwipeCombination[];
    },
    enabled: args.enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

export function useInvalidateWardrobeCombinations() {
  const qc = useQueryClient();
  return (args: {
    userId: string;
    occasion: OccasionKey;
    weather: WeatherKey;
    mood: MoodKey;
  }) => {
    qc.invalidateQueries({ queryKey: wardrobeCombinationsKey(args) });
  };
}
